import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Alert, Spinner } from "react-bootstrap";
import { QRCode } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getReservationStatusApi } from "../../api/api";
import RIcon from "./components/ReservasIcons";
import BeautyConfirmation from "../beauty/BeautyConfirmation";
import {
	getReservationKind,
	readStoredReservation,
} from "./reservationOutcome";
import "./reservas.css";

const FALLBACK_IMAGE =
	"https://images.unsplash.com/photo-1514933651103-005eec06c04b?auto=format&fit=crop&w=800&q=80";

const ReservaSuccess = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [statusInfo, setStatusInfo] = useState(null);
	const [phase, setPhase] = useState("redirect");
	const [reloadKey, setReloadKey] = useState(0);
	const stored = useMemo(() => readStoredReservation(), []);

	const reservationCode = useMemo(() => {
		const fromQuery =
			searchParams.get("code") ||
			searchParams.get("reservationCode") ||
			searchParams.get("orderId") ||
			searchParams.get("oid");
		if (fromQuery) return fromQuery;
		return stored?.reservationCode ? String(stored.reservationCode) : "";
	}, [searchParams, stored]);

	const barId = useMemo(
		() =>
			searchParams.get("barId") ||
			searchParams.get("clubId") ||
			stored?.barId ||
			"",
		[searchParams, stored],
	);

	const queryReservation = useMemo(
		() => ({
			flow: searchParams.get("flow"),
			durationMinutes: searchParams.get("durationMinutes"),
			sport: searchParams.get("sport"),
		}),
		[searchParams],
	);
	const reservationKind = getReservationKind({
		statusInfo,
		queryReservation,
		storedReservation: stored,
	});
	const isCourt = reservationKind === "court";
	useEffect(() => {
		const t = setTimeout(() => setPhase("done"), 1800);
		return () => clearTimeout(t);
	}, []);

	useEffect(() => {
		let mounted = true;
		const loadStatus = async () => {
			setError("");
			if (!reservationCode || !barId) {
				setError("No encontramos el código de reserva.");
				setLoading(false);
				return;
			}
			try {
				setLoading(true);
				const response = await getReservationStatusApi(barId, reservationCode);
				if (!mounted) return;
				setStatusInfo(response?.data?.info || null);
			} catch (err) {
				if (!mounted) return;
				setError(
					err?.response?.data?.message ||
						"No pudimos consultar el estado de la reserva.",
				);
			} finally {
				if (mounted) setLoading(false);
			}
		};
		loadStatus();
		return () => {
			mounted = false;
		};
	}, [reservationCode, barId, reloadKey]);

	const isConfirmed = statusInfo?.status === "confirmed";
	const isPaid = statusInfo?.paymentstatus === "paid";
	const showQr = Boolean(statusInfo?.qrToken && isConfirmed && isPaid);

	if (phase === "redirect") {
		return (
			<div className="reservas-root">
				<div className="wrap">
					<div className="success" style={{ paddingTop: 100 }}>
						<div className="redirect-note">
							<span className="spin" /> Procesando tu reserva…
						</div>
						<h1 style={{ fontSize: 26 }}>Procesando tu reserva</h1>
						<p className="ssub">
							Esperá unos segundos mientras procesamos tu reserva.
						</p>
					</div>
				</div>
			</div>
		);
	}

	if (reservationKind === "beauty") {
		return (
			<BeautyConfirmation
				statusInfo={statusInfo}
				loading={loading}
				error={error}
				reservationCode={reservationCode}
				barId={barId}
				onRetry={() => setReloadKey((k) => k + 1)}
				navigate={navigate}
			/>
		);
	}

	return (
		<div className="reservas-root">
			<div className="wrap">
				<div className="success">
					<div className="tick">
						<div className="ring">
							<RIcon name="check" width={38} height={38} />
						</div>
					</div>
					<h1>
						{isConfirmed
							? isCourt
								? "¡Reserva de cancha confirmada!"
								: "¡Reserva confirmada!"
							: "Reserva en proceso"}
					</h1>
					<p className="ssub">
						{isConfirmed
							? `Tu ${isCourt ? "cancha" : "mesa"} quedó reservada. Te enviamos el comprobante por email.`
							: "Estamos terminando de validar el estado de tu reserva."}
					</p>

					{loading && (
						<div style={{ margin: "12px 0" }}>
							<Spinner animation="border" size="sm" />
						</div>
					)}

					{(reservationCode || statusInfo) && (
						<div className="receipt">
							<div className="receipt-top">
								<img
									src={statusInfo?.barImage || FALLBACK_IMAGE}
									alt={isCourt ? "Club" : "Bar"}
									onError={(e) => {
										e.currentTarget.src = FALLBACK_IMAGE;
									}}
								/>
								<div>
									<div className="rt-name">
										{statusInfo?.barName || (isCourt ? "Tu club" : "Tu bar")}
									</div>
									{statusInfo?.address && (
										<div className="rt-loc">
											<RIcon name="pin" width={14} height={14} />
											{statusInfo.address}
										</div>
									)}
								</div>
								{reservationCode && (
									<div className="rt-code">
										<div className="l">Código</div>
										<div className="c">{reservationCode}</div>
									</div>
								)}
							</div>
							<div className="receipt-grid">
								{statusInfo?.date && (
									<div className="rg">
										<div className="l">
											<RIcon name="cal" width={14} height={14} /> Fecha
										</div>
										<div className="v">
											{dayjs(statusInfo.date).format("DD/MM/YYYY")}
										</div>
									</div>
								)}
								{statusInfo?.slotStart && (
									<div className="rg">
										<div className="l">
											<RIcon name="clock" width={14} height={14} /> Horario
										</div>
										<div className="v">{statusInfo.slotStart} hs</div>
									</div>
								)}
								{isCourt && (
									<>
										{Number(statusInfo?.durationMinutes) > 0 && (
											<div className="rg">
												<div className="l">
													<RIcon name="clock" width={14} height={14} /> Duración
												</div>
												<div className="v">
													{statusInfo.durationMinutes} min
												</div>
											</div>
										)}
										{statusInfo?.tableLabel && (
											<div className="rg">
												<div className="l">
													<RIcon name="ticket" width={14} height={14} /> Cancha
												</div>
												<div className="v">{statusInfo.tableLabel}</div>
											</div>
										)}
									</>
								)}
								{!isCourt && statusInfo?.zone && (
									<div className="rg">
										<div className="l">
											<RIcon name="pin" width={14} height={14} /> Zona
										</div>
										<div className="v">{statusInfo.zone}</div>
									</div>
								)}
								{statusInfo?.depositAmount > 0 && (
									<div className="rg">
										<div className="l">
											<RIcon name="dollar" width={14} height={14} /> Seña
										</div>
										<div className="v">
											$
											{Number(statusInfo.depositAmount).toLocaleString("es-AR")}
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{showQr && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								margin: "8px 0 18px",
							}}
						>
							<QRCode value={statusInfo.qrToken} size={190} />
						</div>
					)}
					{statusInfo && !isConfirmed && (
						<p
							style={{
								fontSize: 13,
								color: "#6b7280",
								fontWeight: 600,
								marginBottom: 16,
							}}
						>
							Estado: <strong>{statusInfo.status}</strong> · Pago:{" "}
							<strong>{statusInfo.paymentstatus}</strong>
						</p>
					)}
					{error && <Alert variant="warning">{error}</Alert>}

					<div className="success-actions">
						<button
							className="r-btn-soft"
							style={{ padding: "13px 24px" }}
							onClick={() =>
								navigate(isCourt ? "/canchas/historial" : "/reservas/historial")
							}
						>
							Ver mis reservas
						</button>
						<button
							className="r-btn-primary"
							style={{ padding: "13px 28px" }}
							onClick={() => navigate(isCourt ? "/canchas" : "/reservas")}
						>
							{isCourt ? "Reservar otra cancha" : "Reservar otra mesa"}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReservaSuccess;
